import { useDeferredValue, useEffect, useRef, useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import {
  AlertTriangle,
  Ban,
  CircleHelp,
  Search,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { InlineError } from '@/components/ui/inline-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PageHeaderActions } from '@/components/layout/page-header'
import {
  useCampaignAccountPicker,
  useCampaignAudiencePreview,
} from '@/hooks/use-campaign-audience'
import {
  CAMPAIGN_CLEARANCE_BADGE,
  CAMPAIGN_EVIDENCE_BADGE,
  CAMPAIGN_KIND_RULES,
  CAMPAIGN_KINDS,
  CAMPAIGN_LAUNCH_BADGE,
  assessCampaignContact,
  campaignAudienceRefusal,
  campaignAudienceShowsNumbers,
  campaignLaunchReadiness,
  campaignMetricsInconsistency,
  campaignSearchValidationError,
  measureCampaignAudience,
  normalizeCampaignSearch,
  resolveCampaignAudienceReadState,
  type CampaignKind,
  type CampaignLaunchReadiness,
} from '@/lib/campaigns'

const searchSchema = z.object({
  kind: z.enum(CAMPAIGN_KINDS).optional().default('call'),
  account_id: z.uuid().optional(),
  include_inactive: z.boolean().optional(),
})

export const Route = createFileRoute('/_authed/campaigns')({
  validateSearch: searchSchema,
  component: CampaignAudiencePreflight,
})

/**
 * Every status badge on this screen is styled from `CampaignBadge` data through
 * an inline `style` on a `variant="secondary"` `<Badge>`, matching
 * `pipeline/stage-badge.tsx` and `accounts/account-badge.tsx`.
 *
 * It is not a free choice. Passing Tailwind classes as `className` to a
 * default-variant `<Badge>` leaves its `hover:bg-primary/80` in place —
 * `tailwind-merge` keys conflicts on (modifier set, class group), so an
 * unmodified `bg-[…]` cannot displace a `hover:` one — and `--primary` is the
 * tenant's raw brand accent (`src/lib/theme.ts:635`). That **replaced** the status
 * tint with the accent on hover — both are the same `background-color` longhand,
 * so the tint was displaced rather than layered under — which DESIGN.md §6 line
 * 102 forbids for badges and §11 treats as an automatic Originality FAIL. See `src/lib/campaigns.ts`'s
 * `CampaignBadge` docblock and the guard in `src/lib/campaigns.test.ts`.
 */

type MetricTone = 'total' | 'evidence' | 'missing' | 'review' | 'blocked'

// DESIGN.md §6: the tenant accent may never appear on icons at rest.
const METRIC_TONE_CLASS: Record<MetricTone, string> = {
  total: 'text-[color:var(--ink-3)]',
  evidence: 'text-[color:var(--status-quoted-fg)]',
  missing: 'text-[color:var(--status-expired-fg)]',
  review: 'text-[color:var(--status-on-hold-fg)]',
  blocked: 'text-[color:var(--status-lost-fg)]',
}

const FILTER_SELECT_CLASS =
  'h-8 w-full rounded-md border border-[color:var(--interactive-border)] bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'

function CampaignAudiencePreflight() {
  const navigate = useNavigate({ from: Route.fullPath })
  const routeSearch = Route.useSearch()
  const [searchText, setSearchText] = useState('')
  const [audienceSearch, setAudienceSearch] = useState<string>()
  const [accountSearch, setAccountSearch] = useState('')
  const deferredAccountSearch = useDeferredValue(accountSearch)
  const normalizedAccountSearch = normalizeCampaignSearch(accountSearch)
  const normalizedDeferredAccountSearch = normalizeCampaignSearch(
    deferredAccountSearch,
  )
  const accountSearchError = campaignSearchValidationError(
    normalizedAccountSearch,
  )
  const deferredAccountSearchError = campaignSearchValidationError(
    normalizedDeferredAccountSearch,
  )
  const normalizedSearchText = normalizeCampaignSearch(searchText)
  const searchInputError = campaignSearchValidationError(normalizedSearchText)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const audienceHeadingRef = useRef<HTMLHeadingElement>(null)
  const accountsQuery = useCampaignAccountPicker({
    enabled: !accountSearchError && !deferredAccountSearchError,
    search: normalizedDeferredAccountSearch,
    selectedAccountId: routeSearch.account_id,
  })
  const accounts = accountsQuery.data ?? []

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    [],
  )

  const audienceQuery = useCampaignAudiencePreview({
    accountId: routeSearch.account_id,
    includeInactive: routeSearch.include_inactive ?? false,
    search: audienceSearch,
  }, { enabled: !searchInputError })

  // The typed search has not reached the query yet. Everything already on
  // screen answers a different question, so it must not be shown as though it
  // answered this one.
  const searchPending = !searchInputError && normalizedSearchText !== audienceSearch
  const rule = CAMPAIGN_KIND_RULES[routeSearch.kind]
  const rows = audienceQuery.data?.rows ?? []
  const total = audienceQuery.data?.total ?? 0
  const metrics = measureCampaignAudience(routeSearch.kind, rows)
  // Render-boundary backstop. `decodeCampaignAudiencePayload` already refuses
  // any page that is not exactly min(total, limit) rows, so this should never
  // fire today — it exists so that a future change to the hook (cached pages,
  // client-side filtering, a different projection) degrades into a visible
  // refusal instead of a quietly wrong count.
  const metricsProblem = audienceQuery.data
    ? campaignMetricsInconsistency(
        metrics,
        total,
        audienceQuery.data.limit,
      )
    : null
  const readiness = campaignLaunchReadiness(routeSearch.kind, metrics, total)
  const hasFilters = Boolean(
    routeSearch.account_id ||
      normalizedSearchText ||
      audienceSearch ||
      routeSearch.include_inactive,
  )
  // The single source of truth for "may this screen show a number?".
  //
  // This replaces a boolean assembled from four negatives
  // (`!busy && !searchError && !isError && !metricsProblem`), which fails open:
  // all four are false when no read has happened at all. A paused TanStack
  // query — `status: 'pending'`, `fetchStatus: 'paused'`, `data: undefined`,
  // which is what the default `networkMode: 'online'` produces offline — passed
  // every one of them and put "Accessible contacts 0" on screen for a request
  // that never left the browser.
  //
  // `resolveCampaignAudienceReadState` asks the question positively instead and
  // is exhaustively unit-tested, including that exact input.
  const audienceReadState = resolveCampaignAudienceReadState({
    status: audienceQuery.status,
    fetchStatus: audienceQuery.fetchStatus,
    hasData: audienceQuery.data !== undefined,
    searchSettled: !searchPending,
    searchValid: !searchInputError,
    metricsConsistent: metricsProblem === null,
  })
  const countsTrustworthy = campaignAudienceShowsNumbers(audienceReadState)
  // `loading` is the only state that shows a skeleton. `paused` deliberately
  // does not: a stalled fetch that will never complete on its own must not
  // spin forever pretending progress.
  const audienceBusy = audienceReadState === 'loading'
  const previewLabel = readiness.partial ? 'Preview ' : ''

  const evidenceTiles: { label: string; value: number; tone: MetricTone }[] =
    rule.channel === 'none'
      ? [
          {
            label: rule.evidenceUnverifiedLabel,
            value: metrics.evidenceUnverified,
            tone: 'missing',
          },
        ]
      : [
          {
            label: rule.evidencePresentLabel,
            value: metrics.evidencePresent,
            tone: 'evidence',
          },
          {
            label: rule.evidenceMissingLabel,
            value: metrics.evidenceMissing,
            tone: 'missing',
          },
        ]

  const tiles: {
    label: string
    value: number
    tone: MetricTone
    icon: React.ReactNode
    scoped: boolean
  }[] = [
    {
      label: 'Accessible contacts',
      value: total,
      tone: 'total',
      icon: <Users className="h-4 w-4" aria-hidden="true" />,
      scoped: false,
    },
    ...evidenceTiles.map((tile) => ({
      ...tile,
      icon: <CircleHelp className="h-4 w-4" aria-hidden="true" />,
      scoped: true,
    })),
    {
      label: 'Needs clearance review',
      value: metrics.clearanceReview,
      tone: 'review' as const,
      icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
      scoped: true,
    },
    {
      label: 'Blocked for this type',
      value: metrics.clearanceBlocked,
      tone: 'blocked' as const,
      icon: <Ban className="h-4 w-4" aria-hidden="true" />,
      scoped: true,
    },
  ]

  function updateSearch(
    next: Partial<z.infer<typeof searchSchema>>,
  ) {
    void navigate({
      to: '/campaigns',
      search: (previous) => ({ ...previous, ...next }),
    })
  }

  function handleSearch(value: string) {
    setSearchText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      const normalized = normalizeCampaignSearch(value)
      if (campaignSearchValidationError(normalized)) return
      setAudienceSearch(normalized)
    }, 200)
  }

  // Every clear control unmounts itself as a direct result of its own click,
  // which would drop focus to <body> and lose a keyboard user's place. Focus
  // moves to the region whose content changed instead.
  function clearFilters() {
    clearAudienceSearch({ focus: false })
    void navigate({
      to: '/campaigns',
      search: { kind: routeSearch.kind },
    })
    audienceHeadingRef.current?.focus()
  }

  function clearAudienceSearch({ focus = true }: { focus?: boolean } = {}) {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    setSearchText('')
    setAudienceSearch(undefined)
    if (focus) searchInputRef.current?.focus()
  }

  /**
   * Every "Try again" on this screen destroys itself.
   *
   * A retry from `unread` is the sharpest case: `refetch()` sets
   * `fetchStatus: 'fetching'` while `status` stays `'pending'`, so the read
   * state flips to `loading` on the *same* render pass and the refusal — with
   * the button inside it — unmounts synchronously with its own activation.
   * `paused`, `inconsistent` and the `InlineError` retry unmount a moment
   * later, on success. In all four cases focus would land on `<body>`: a
   * keyboard user is thrown to the top of the document and a screen-reader
   * virtual cursor resets, forcing a re-traverse of every filter to get back
   * to the table.
   *
   * Focus moves to the region whose content is about to change, which is the
   * same handoff `clearFilters` already performs.
   */
  function retryAudience() {
    void audienceQuery.refetch()
    audienceHeadingRef.current?.focus()
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeaderActions>
        <Badge variant="secondary">Beta</Badge>
      </PageHeaderActions>

      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-[1440px] space-y-4 px-4 py-4 md:px-6">
          {/*
            `CardTitle` renders a `<div>` (src/components/ui/card.tsx:32-44), so
            both cards below used to present as unlabelled regions with no
            heading of their own — a screen-reader user landing here got two
            anonymous groups between the page title and "Audience preview".

            Fixed locally rather than by making `CardTitle` a heading: four
            other routes consume that primitive (accounts detail, dashboard,
            both profile routes) and each would inherit a heading at whatever
            level this route happens to need, silently reshaping their outlines.
            The visual classes are copied verbatim from `CardTitle`, so this is
            a semantics-only change with no pixel delta.
          */}
          <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
            {/*
              `role="region"` on the Card rather than a wrapping `<section>`:
              these two Cards are direct grid children and stretch to the row
              height, which an interposed element would change. `region` is the
              exact role a named `<section>` maps to, so the accessibility tree
              is identical and the layout is untouched.
            */}
            <Card role="region" aria-labelledby="campaign-preflight-heading">
              <CardHeader>
                <h2
                  id="campaign-preflight-heading"
                  className="text-[14px] font-[560] leading-5 tracking-[-0.005em]"
                >
                  Audience preflight
                </h2>
                <CardDescription>
                  Define an audience and expose gaps before work begins.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="campaign-kind">Campaign type</Label>
                  <select
                    id="campaign-kind"
                    aria-label="Campaign type"
                    className={FILTER_SELECT_CLASS}
                    value={routeSearch.kind}
                    onChange={(event) =>
                      updateSearch({
                        kind: event.target.value as CampaignKind,
                      })
                    }
                  >
                    {CAMPAIGN_KINDS.map((kind) => (
                      <option key={kind} value={kind}>
                        {CAMPAIGN_KIND_RULES[kind].label}
                      </option>
                    ))}
                  </select>
                </div>

                {/*
                  Two controls, one visible caption. A <label htmlFor> can only
                  name one of them, and whichever it named the other would look
                  captioned but be unlabelled — so the caption names the group
                  and each control keeps its own accessible name.
                */}
                <div
                  className="space-y-1.5"
                  role="group"
                  aria-labelledby="campaign-account-caption"
                >
                  <p
                    id="campaign-account-caption"
                    className="text-sm font-medium leading-none"
                  >
                    Account
                  </p>
                  <Input
                    id="campaign-account-search"
                    aria-label="Search campaign accounts"
                    value={accountSearch}
                    maxLength={120}
                    placeholder="Search account names"
                    aria-invalid={accountSearchError ? true : undefined}
                    aria-describedby={
                      accountSearchError
                        ? 'campaign-account-search-error'
                        : undefined
                    }
                    onChange={(event) => setAccountSearch(event.target.value)}
                  />
                  {accountSearchError && (
                    <p
                      id="campaign-account-search-error"
                      className="text-[11px] text-[color:var(--status-lost-fg)]"
                    >
                      {accountSearchError}
                    </p>
                  )}
                  <select
                    id="campaign-account"
                    aria-label="Campaign account"
                    className={FILTER_SELECT_CLASS}
                    value={routeSearch.account_id ?? '__all__'}
                    disabled={accountsQuery.isError || Boolean(accountSearchError)}
                    onChange={(event) =>
                      updateSearch({
                        account_id:
                          event.target.value === '__all__'
                            ? undefined
                            : event.target.value,
                      })
                    }
                  >
                    <option value="__all__">All accounts</option>
                    {routeSearch.account_id &&
                      !accounts.some(
                        (account) => account.id === routeSearch.account_id,
                      ) && (
                        <option value={routeSearch.account_id}>
                          {accountSearchError
                            ? 'Fix account search to load selection'
                            : accountsQuery.isPending
                            ? 'Loading selected account…'
                            : 'Selected account unavailable'}
                        </option>
                      )}
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                  {accountsQuery.isError && !accountSearchError && (
                    <p className="text-[11px] text-[color:var(--status-lost-fg)]">
                      Account filter unavailable
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="campaign-search">Find contacts</Label>
                  <div className="relative">
                    <Search
                      className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-[color:var(--ink-3)]"
                      aria-hidden="true"
                    />
                    <Input
                      id="campaign-search"
                      ref={searchInputRef}
                      value={searchText}
                      maxLength={120}
                      placeholder="Name or email"
                      className="pl-8"
                      aria-invalid={searchInputError ? true : undefined}
                      aria-describedby={
                        searchInputError ? 'campaign-search-error' : undefined
                      }
                      onChange={(event) => handleSearch(event.target.value)}
                    />
                  </div>
                  {searchInputError && (
                    <p
                      id="campaign-search-error"
                      className="text-[11px] text-[color:var(--status-lost-fg)]"
                    >
                      {searchInputError}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 md:col-span-3">
                  <Button
                    variant={
                      routeSearch.include_inactive ? 'secondary' : 'outline'
                    }
                    size="sm"
                    aria-pressed={routeSearch.include_inactive ?? false}
                    onClick={() =>
                      updateSearch({
                        include_inactive: routeSearch.include_inactive
                          ? undefined
                          : true,
                      })
                    }
                  >
                    Include inactive
                  </Button>
                  {hasFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="mr-1 h-4 w-4" aria-hidden="true" />
                      Clear filters
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card role="region" aria-labelledby="campaign-kind-heading">
              <CardHeader>
                {/*
                  The heading text is the *selected* kind's label, so this
                  region renames itself when the campaign type changes — which
                  is the point: the explanation below it is about that kind and
                  nothing else.
                */}
                <h2
                  id="campaign-kind-heading"
                  className="text-[14px] font-[560] leading-5 tracking-[-0.005em]"
                >
                  {rule.label}
                </h2>
                <CardDescription>{rule.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3 rounded-md border border-[color:var(--line)] bg-[color:var(--surface-2)] p-3">
                  <ShieldCheck
                    className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--ink-3)]"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="text-[13px] font-[550]">Planning only</p>
                    <p className="mt-1 text-[12px] leading-5 text-[color:var(--ink-3)]">
                      Nothing here is saved, sent, dialed, or exported. Email
                      presence is not consent; phone presence is not do-not-call
                      clearance.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <LaunchReadinessPanel
            countsTrustworthy={countsTrustworthy}
            readiness={readiness}
          />

          <section aria-labelledby="campaign-audience-heading">
            <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
              <div>
                <h2
                  id="campaign-audience-heading"
                  ref={audienceHeadingRef}
                  tabIndex={-1}
                  // `ring-offset-background` is not optional here: without it
                  // Tailwind's default `--tw-ring-offset-color: #fff` paints a
                  // white band on the ring's inner edge in dark mode, which is
                  // not the surface DESIGN.md §7 graded the ring against.
                  //
                  // It works on THIS element specifically because a heading is
                  // not a form control. An earlier revision of this comment
                  // cited `FILTER_SELECT_CLASS` and `input.tsx` as precedents
                  // that "already carry it"; they carry the class but it does
                  // not win. `@tailwindcss/forms` is loaded with the `base`
                  // strategy (`tailwind.config.ts:3,79`), and its
                  // `select:focus` / `input:focus` rules at specificity (0,1,1)
                  // beat `.ring-offset-background` at (0,1,0) — hardcoding the
                  // band to `#fff` and the border to `#2563EB` regardless of
                  // mode or tenant. DESIGN.md §7 records exactly that as an
                  // open, measured exception (2.504 / 2.757 / 2.426 dark-mode
                  // ring-vs-band, below the 3:1 floor) and tracks it as R5.
                  // Note `e2e/campaigns.spec.ts:1107` samples only the ring
                  // COLOUR, which wins at (0,2,0), so a green run there is not
                  // evidence about the offset band on those two controls.
                  className="text-[15px] font-[560] tracking-[-0.005em] ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2"
                >
                  Audience preview
                </h2>
                {/*
                  Same gate as the metrics card, and it now reads off the same
                  resolved state rather than re-deriving it. On a failed read
                  this line used to fall through to "Assessing all 0 accessible
                  contacts" — and on a failed refetch, to the previous run's
                  count, describing an assessment no longer on screen. Either is
                  exactly the fabricated number this surface exists to refuse.

                  `campaignAudienceRefusal` returns null only for `ready`, so
                  the numeric branch below is unreachable in every other state.
                */}
                <p className="mt-0.5 text-[12px] text-[color:var(--ink-3)]">
                  {campaignAudienceRefusal(audienceReadState) ??
                    (total === 0
                      ? 'These filters reach no contacts, so there is nothing to assess.'
                      : readiness.partial
                        ? `Assessing the first ${metrics.assessed.toLocaleString()} of ${total.toLocaleString()} accessible contacts.`
                        : `Assessing all ${total.toLocaleString()} accessible contacts.`)}
                </p>
              </div>
            </div>

            <div aria-live="polite">
              {audienceReadState === 'invalid_search' ? null : audienceBusy ? (
                <div
                  className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4"
                  role="status"
                  aria-busy="true"
                  aria-label="Loading audience metrics"
                >
                  {/*
                    A live region announces the text of what was inserted, not
                    the region's own name — without this the withdrawal of the
                    previous answer is silent to a screen reader while being
                    obvious to everyone else.
                  */}
                  <span className="sr-only">
                    Loading audience metrics. The previous results no longer
                    apply.
                  </span>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <MetricSkeleton key={index} />
                  ))}
                </div>
              ) : countsTrustworthy ? (
                <Card className="mb-3">
                  <CardContent
                    className={
                      tiles.length === 5
                        ? 'grid p-0 sm:grid-cols-2 lg:grid-cols-5'
                        : 'grid p-0 sm:grid-cols-2 lg:grid-cols-4'
                    }
                    data-testid="campaign-audience-metrics"
                  >
                    {tiles.map((tile) => (
                      <Metric
                        key={tile.label}
                        label={`${tile.scoped ? previewLabel : ''}${tile.label}`}
                        value={tile.value}
                        icon={tile.icon}
                        tone={tile.tone}
                      />
                    ))}
                  </CardContent>
                </Card>
              ) : null}
            </div>

            <div className="rounded-lg border border-[color:var(--line)] bg-[color:var(--surface)]">
              {/*
                Every branch below is keyed off the one resolved read state, in
                its precedence order, so the panel cannot disagree with the
                metrics card about what happened.
              */}
              {audienceReadState === 'invalid_search' ? (
                <AudienceRefusal
                  testid="campaign-search-refusal"
                  title="Campaign search is too complex"
                  description={`${searchInputError} Clear the search before assessing an audience.`}
                  action={
                    <Button size="sm" onClick={() => clearAudienceSearch()}>
                      Clear search
                    </Button>
                  }
                />
              ) : audienceReadState === 'error' ? (
                <div className="p-6">
                  <InlineError
                    what="campaign audience"
                    onRetry={retryAudience}
                  />
                </div>
              ) : audienceReadState === 'paused' ? (
                /*
                  The state that used to render "0 accessible contacts". The
                  request never left the browser, so the only honest thing to
                  say is that nothing was read — not that nothing matched.
                */
                <AudienceRefusal
                  testid="campaign-paused-refusal"
                  /*
                    Deliberately NOT "this audience hasn't been read": a paused
                    refetch can follow a successful read, in which case the
                    audience *was* read, just not re-read. What is true in both
                    cases is that the server cannot confirm it right now.
                  */
                  title="Can’t reach the server"
                  description="This device appears to be offline, so these numbers can’t be confirmed. Reconnect and try again."
                  action={
                    <Button size="sm" onClick={retryAudience}>
                      Try again
                    </Button>
                  }
                />
              ) : audienceReadState === 'unread' ? (
                <AudienceRefusal
                  testid="campaign-unread-refusal"
                  title="This audience hasn’t been read"
                  description="No result has come back for these filters yet, so no count can be shown."
                  action={
                    <Button size="sm" onClick={retryAudience}>
                      Try again
                    </Button>
                  }
                />
              ) : audienceReadState === 'inconsistent' ? (
                <AudienceRefusal
                  testid="campaign-inconsistent-refusal"
                  title="These audience numbers don’t add up"
                  description={`${metricsProblem} Nothing is shown rather than showing a count we can’t stand behind.`}
                  action={
                    <Button size="sm" onClick={retryAudience}>
                      Try again
                    </Button>
                  }
                />
              ) : !audienceBusy && rows.length === 0 ? (
                <EmptyState
                  title={
                    hasFilters
                      ? 'No contacts match this audience'
                      : 'No contacts available'
                  }
                  description={
                    hasFilters
                      ? 'Adjust or clear the filters to widen this campaign audience.'
                      : 'Add contacts before planning a campaign audience.'
                  }
                  action={
                    hasFilters ? (
                      <Button size="sm" onClick={clearFilters}>
                        Clear filters
                      </Button>
                    ) : (
                      <Button asChild size="sm">
                        <Link to="/contacts">Open contacts</Link>
                      </Button>
                    )
                  }
                />
              ) : (
                <Table
                  aria-label="Campaign audience preview"
                  aria-busy={audienceBusy || undefined}
                >
                  <TableHeader>
                    <TableRow>
                      <TableHead>Contact</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Contact details on file</TableHead>
                      <TableHead>Cleared to act?</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {audienceBusy
                      ? Array.from({ length: 6 }).map((_, index) => (
                          <TableRow key={index}>
                            {Array.from({ length: 4 }).map((__, cellIndex) => (
                              <TableCell key={cellIndex}>
                                <div
                                  aria-hidden="true"
                                  className="h-4 animate-pulse rounded bg-[color:var(--surface-2)]"
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : rows.map((contact) => {
                          const assessment = assessCampaignContact(
                            routeSearch.kind,
                            contact,
                          )
                          const evidence =
                            CAMPAIGN_EVIDENCE_BADGE[assessment.evidence]
                          const clearance =
                            CAMPAIGN_CLEARANCE_BADGE[assessment.clearance]
                          const contactName =
                            `${contact.first_name} ${contact.last_name}`.trim() ||
                            'Unnamed contact'

                          return (
                            <TableRow
                              key={contact.id}
                              data-outcome={assessment.outcome}
                            >
                              <TableCell>
                                <Link
                                  to="/contacts/$contactId"
                                  params={{ contactId: contact.id }}
                                  className="font-[550] text-[color:var(--accent-text)] hover:underline"
                                >
                                  {contactName}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {contact.account ? (
                                  <Link
                                    to="/accounts/$accountId"
                                    params={{ accountId: contact.account.id }}
                                    className="hover:underline"
                                  >
                                    {contact.account.name}
                                  </Link>
                                ) : (
                                  <span className="text-[color:var(--ink-3)]">—</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant="secondary"
                                    style={{
                                      color: evidence.fg,
                                      backgroundColor: evidence.bg,
                                    }}
                                  >
                                    {evidence.label}
                                  </Badge>
                                  <span className="text-[12px] text-[color:var(--ink-3)]">
                                    {assessment.evidenceReason}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge
                                    variant="secondary"
                                    style={{
                                      color: clearance.fg,
                                      backgroundColor: clearance.bg,
                                    }}
                                  >
                                    {clearance.label}
                                  </Badge>
                                  <span className="text-[12px] text-[color:var(--ink-3)]">
                                    {assessment.clearanceReason}
                                  </span>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                  </TableBody>
                </Table>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

/**
 * A refusal the screen must *announce*, with its recovery action deliberately
 * outside the announced region.
 *
 * The r6 revision dropped the `role="alert"` this restores, on the reasoning
 * that "an alert region should not host focusable content, and the input
 * already announces the same message through its own aria-describedby". The
 * first half is correct; the second is not — a description added to an element
 * the user is *already focused in* is not reliably announced (VoiceOver/Safari
 * does not, NVDA is version-dependent). The result was a screen that visibly
 * collapsed into a refusal while saying nothing at all to a screen reader.
 *
 * So the message is an alert and the button is its sibling: nothing focusable
 * inside the live region, and the refusal is still spoken.
 *
 * The markup below reproduces `EmptyState`'s internals verbatim
 * (`src/components/ui/empty-state.tsx:25-37`) rather than wrapping it, because
 * `EmptyState` renders its `action` inside the same box as its text and there
 * is no prop that separates them. Same classes, same order, same spacing — the
 * only change is which subtree carries the role.
 */
function AudienceRefusal({
  title,
  description,
  action,
  testid,
}: {
  title: string
  description: string
  action?: React.ReactNode
  testid: string
}) {
  return (
    <div className="p-6">
      <div className="max-w-[420px] py-6">
        <div role="alert" data-testid={testid}>
          <p className="text-[14px] font-[560] text-[color:var(--ink)]">
            {title}
          </p>
          <p className="mt-1 text-[13px] leading-5 text-[color:var(--ink-3)]">
            {description}
          </p>
        </div>
        {action ? <div className="mt-3">{action}</div> : null}
      </div>
    </div>
  )
}

function LaunchReadinessPanel({
  countsTrustworthy,
  readiness,
}: {
  countsTrustworthy: boolean
  readiness: CampaignLaunchReadiness
}) {
  return (
    <section aria-labelledby="campaign-readiness-heading">
      <Card data-testid="campaign-launch-readiness">
        <CardHeader className="flex-row items-start gap-3 space-y-0">
          <div className="min-w-0 flex-1">
            <h2
              id="campaign-readiness-heading"
              className="text-[15px] font-[560] tracking-[-0.005em]"
            >
              Can this campaign launch?
            </h2>
            <CardDescription className="mt-0.5">
              {readiness.headline}
            </CardDescription>
          </div>
          <Badge
            variant="secondary"
            className="shrink-0"
            style={{
              color: CAMPAIGN_LAUNCH_BADGE.fg,
              backgroundColor: CAMPAIGN_LAUNCH_BADGE.bg,
            }}
            data-testid="campaign-launch-state"
          >
            {CAMPAIGN_LAUNCH_BADGE.label}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          {/*
            The counts describe rows we do not currently have, so they are
            withheld. The unknowns and blockers are properties of the campaign
            type, not of the data, and stay true either way.
          */}
          <ReadinessList
            title="What your CRM already shows"
            items={
              countsTrustworthy
                ? readiness.known
                : ['Contact counts are unavailable right now.']
            }
            testid="campaign-readiness-known"
          />
          <ReadinessList
            title="What it can’t confirm yet"
            items={readiness.unknown}
            testid="campaign-readiness-unknown"
          />
          <ReadinessList
            title="What has to exist before launch"
            items={readiness.blockers}
            testid="campaign-readiness-blockers"
          />
        </CardContent>
        <CardContent className="pt-0">
          <p className="text-[12px] leading-5 text-[color:var(--ink-3)]">
            {readiness.betaReason}
          </p>
        </CardContent>
      </Card>
    </section>
  )
}

function ReadinessList({
  title,
  items,
  testid,
}: {
  title: string
  items: readonly string[]
  testid: string
}) {
  return (
    <div>
      <p
        id={`${testid}-title`}
        className="text-[11px] font-[550] uppercase tracking-[0.04em] text-[color:var(--ink-3)]"
      >
        {title}
      </p>
      <ul
        className="mt-2 space-y-1.5"
        data-testid={testid}
        aria-labelledby={`${testid}-title`}
      >
        {items.map((item) => (
          <li
            key={item}
            className="flex gap-2 text-[13px] leading-5 text-[color:var(--ink-2)]"
          >
            <span aria-hidden="true" className="text-[color:var(--ink-3)]">
              ·
            </span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Metric({
  label,
  value,
  icon,
  tone,
}: {
  label: string
  value: number
  icon: React.ReactNode
  tone: MetricTone
}) {
  return (
    <div className="flex items-center gap-3 border-b border-[color:var(--line)] p-3 last:border-b-0 sm:[&:nth-child(odd)]:border-r lg:border-b-0 lg:border-r lg:last:border-r-0">
      <div className={METRIC_TONE_CLASS[tone]}>{icon}</div>
      <div>
        <p className="font-mono text-[20px] font-[500] tabular-nums">
          {value.toLocaleString()}
        </p>
        <p className="text-[11px] font-[550] uppercase tracking-[0.04em] text-[color:var(--ink-3)]">
          {label}
        </p>
      </div>
    </div>
  )
}

function MetricSkeleton() {
  return (
    <div className="h-[68px] animate-pulse rounded-lg border border-[color:var(--line)] bg-[color:var(--surface)] p-3">
      <div className="h-4 w-12 rounded bg-[color:var(--surface-2)]" />
      <div className="mt-2 h-3 w-24 rounded bg-[color:var(--surface-2)]" />
    </div>
  )
}
